import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { BottomTabBar } from '@/components/layout/BottomTabBar'
import { apiUrl } from '@/lib/api'

interface Student {
  id: string
  studentNumber: number
  prefix: string
  fullName: string
  nickname: string | null
  note: string | null
  active: boolean
  lineLinked: boolean
}

interface GuardianLink {
  linkId: string
  guardianId: string
  lineDisplayName: string
  linePictureUrl: string | null
  fullName: string | null
  relationship: string | null
  phone: string | null
  isPrimary: boolean
  canNotify: boolean
}

export function StudentDetailPage() {
  const { studentId } = useParams()
  const navigate = useNavigate()
  const [student, setStudent] = useState<Student | null>(null)
  const [guardians, setGuardians] = useState<GuardianLink[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [confirmUnlink, setConfirmUnlink] = useState<string | null>(null)
  const [confirmDeactivate, setConfirmDeactivate] = useState(false)
  const [toast, setToast] = useState<string | null>(null)

  useEffect(() => {
    if (!studentId) return
    Promise.all([
      fetch(apiUrl(`/api/students/${studentId}`), { credentials: 'include' }).then((r) => r.json()),
      fetch(apiUrl(`/api/students/${studentId}/guardians`), { credentials: 'include' }).then((r) => r.json()),
    ])
      .then(([studentRes, guardianRes]) => {
        if (studentRes.success) setStudent(studentRes.data)
        if (guardianRes.success) setGuardians(guardianRes.data)
      })
      .catch(() => {})
      .finally(() => setIsLoading(false))
  }, [studentId])

  async function handleUnlink(linkId: string) {
    const res = await fetch(apiUrl(`/api/student-guardians/${linkId}`), {
      method: 'DELETE',
      credentials: 'include',
    })
    const data = await res.json()
    if (data.success) {
      setGuardians((prev) => prev.filter((g) => g.linkId !== linkId))
      showToast('ยกเลิกการเชื่อมต่อแล้ว')
    }
    setConfirmUnlink(null)
  }

  async function handleDeactivate() {
    if (!studentId) return
    const res = await fetch(apiUrl(`/api/students/${studentId}/deactivate`), {
      method: 'PATCH',
      credentials: 'include',
    })
    const data = await res.json()
    if (data.success) {
      showToast('ปิดใช้งานนักเรียนแล้ว')
      setTimeout(() => navigate('/students'), 800)
    }
    setConfirmDeactivate(false)
  }

  function showToast(msg: string) {
    setToast(msg)
    setTimeout(() => setToast(null), 2000)
  }

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-gray-400">กำลังโหลด...</div>
      </div>
    )
  }

  if (!student) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-gray-400">ไม่พบข้อมูลนักเรียน</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <header className="bg-white px-4 py-3 shadow-sm">
        <div className="mx-auto flex max-w-lg items-center gap-3">
          <button onClick={() => navigate('/students')} className="text-gray-600">
            ⟨
          </button>
          <h1 className="text-lg font-bold text-gray-900">ข้อมูลนักเรียน</h1>
        </div>
      </header>

      <main className="mx-auto max-w-lg px-4 py-4 space-y-4">
        {/* Student Info Card */}
        <div className="rounded-lg bg-white p-4 shadow-sm">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs text-gray-400">เลขที่ {student.studentNumber}</p>
              <p className="text-lg font-bold text-gray-900">
                {student.prefix}{student.fullName}
              </p>
              {student.nickname && (
                <p className="text-sm text-gray-500">ชื่อเล่น: {student.nickname}</p>
              )}
              {student.note && (
                <p className="mt-1 text-sm text-gray-400">หมายเหตุ: {student.note}</p>
              )}
            </div>
            <span
              className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                student.lineLinked
                  ? 'bg-green-50 text-green-700'
                  : 'bg-orange-50 text-orange-700'
              }`}
            >
              {student.lineLinked ? 'LINE linked' : 'ยังไม่ link'}
            </span>
          </div>

          <div className="mt-3 flex gap-2">
            <button
              onClick={() => navigate(`/students/${studentId}/edit`)}
              className="flex-1 rounded-lg border border-gray-200 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              แก้ไข
            </button>
            <button
              onClick={() => setConfirmDeactivate(true)}
              className="rounded-lg border border-red-200 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
            >
              ปิดใช้งาน
            </button>
          </div>
        </div>

        {/* Guardians */}
        <div>
          <h2 className="mb-2 text-sm font-semibold text-gray-700">
            ผู้ปกครอง ({guardians.length})
          </h2>

          {guardians.length === 0 ? (
            <div className="rounded-lg bg-white p-4 text-center text-sm text-gray-400 shadow-sm">
              ยังไม่มีผู้ปกครองเชื่อมต่อ
            </div>
          ) : (
            <div className="space-y-2">
              {guardians.map((g) => (
                <div key={g.linkId} className="rounded-lg bg-white p-4 shadow-sm">
                  <div className="flex items-start gap-3">
                    {/* Avatar */}
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-sm text-gray-500">
                      {g.linePictureUrl ? (
                        <img
                          src={g.linePictureUrl}
                          alt=""
                          className="h-10 w-10 rounded-full object-cover"
                        />
                      ) : (
                        '👤'
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">
                        {g.fullName || g.lineDisplayName}
                      </p>
                      {g.relationship && (
                        <p className="text-xs text-gray-500">ความสัมพันธ์: {g.relationship}</p>
                      )}
                      {g.phone && (
                        <p className="text-xs text-gray-500">เบอร์: {g.phone}</p>
                      )}
                      <div className="mt-1 flex gap-2">
                        {g.isPrimary && (
                          <span className="rounded bg-blue-50 px-1.5 py-0.5 text-xs text-blue-600">
                            หลัก
                          </span>
                        )}
                        {g.canNotify && (
                          <span className="rounded bg-green-50 px-1.5 py-0.5 text-xs text-green-600">
                            แจ้งเตือน
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => setConfirmUnlink(g.linkId)}
                      className="text-xs text-red-500 hover:text-red-700"
                    >
                      ยกเลิก
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Unlink confirmation */}
      {confirmUnlink && (
        <ConfirmDialog
          title="ยกเลิกการเชื่อมต่อ"
          message="ต้องการยกเลิกการเชื่อมต่อผู้ปกครองคนนี้? ผู้ปกครองจะไม่ได้รับการแจ้งเตือนอีก"
          confirmLabel="ยกเลิกการเชื่อมต่อ"
          onConfirm={() => handleUnlink(confirmUnlink)}
          onCancel={() => setConfirmUnlink(null)}
        />
      )}

      {/* Deactivate confirmation */}
      {confirmDeactivate && (
        <ConfirmDialog
          title="ปิดใช้งานนักเรียน"
          message="ต้องการปิดใช้งานนักเรียนคนนี้? ข้อมูลจะไม่ถูกลบ แต่จะไม่แสดงในรายชื่อ"
          confirmLabel="ปิดใช้งาน"
          onConfirm={handleDeactivate}
          onCancel={() => setConfirmDeactivate(false)}
        />
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed left-1/2 top-4 z-50 -translate-x-1/2 rounded-lg bg-gray-800 px-4 py-2 text-sm text-white shadow-lg">
          {toast}
        </div>
      )}

      <BottomTabBar active="students" />
    </div>
  )
}

function ConfirmDialog({
  title,
  message,
  confirmLabel,
  onConfirm,
  onCancel,
}: {
  title: string
  message: string
  confirmLabel: string
  onConfirm: () => void
  onCancel: () => void
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-sm rounded-xl bg-white p-5 shadow-xl">
        <h3 className="text-base font-bold text-gray-900">{title}</h3>
        <p className="mt-2 text-sm text-gray-600">{message}</p>
        <div className="mt-4 flex gap-2">
          <button
            onClick={onCancel}
            className="flex-1 rounded-lg border border-gray-200 py-2.5 text-sm font-medium text-gray-600"
          >
            ยกเลิก
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 rounded-lg bg-red-600 py-2.5 text-sm font-semibold text-white"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
